import { useState, useCallback, useRef } from "react";
import Loader from "react-loader-spinner";
import { useDebouncedPage } from "./hooks/useDebouncedPage";
import Restricted from "PermissionProvider/Restricted";
import { useEvaluationQuery } from "./hooks/useEvaluationQuery";
import { useEvaluationFilter } from "./hooks/useEvaluationFilter";
import { Pagination } from "react-bootstrap";
import SelectWithLabel from "components/UI/SelectWithLabel";
import EvaluationListTranslation from "./EvaluationListTranslation";
import EvaluationModal from "./EvaluationModal";
import EvaluationEditModal from "./EvaluationEditModal";
import { DropdownSelect } from "components/UI/ComplexComponents/DropdownSelect";

export default function EvaluationList(props) {

    const initialState = {
        idSelected: null,
        dateSelected: null,
        typeSelected: null,
        departmentSelected: null,
        universitySelected: null,
        erasmusCodeSelected: null,
        countrySelected: null,
        departmentalCoordinatorSelected: null,
        sortSelected: null,
        isModalOpen: false
    }

    const getCookie = (name) => {
        const cookie = {}
        document.cookie.split(';').forEach((e) => {
            const [key, value] = e.split('=');
            cookie[key.trim()] = value;
        })
        return cookie[name];
    }

    const [langCode, setLangCode] = useState(getCookie("lang") ? getCookie("lang") : "en")
    const [selectedRow, setSelectedRow] = useState(null)

    const filters = useEvaluationFilter(initialState)
    const {
        state,
        setDateSelected,
        setDepartmentSelected,
        setUniversitySelected,
        setErasmusCodeSelected,
        setCountrySelected,
        setDepartmentalCoordinatorSelected,
        setSortSelected,
        setIsModalOpen
    } = filters;

    const {
        dateSelected,
        departmentSelected,
        universitySelected,
        erasmusCodeSelected,
        countrySelected,
        departmentalCoordinatorSelected,
        sortSelected,
        isModalOpen
    } = state;

    const { evaluation, loading, page, departments, countries, departmentalCoordinators, setPage } = useEvaluationQuery(filters, props.userService, langCode)

    const [isModalEditOpen, setIsModalEditOpen] = useState(false);
    const setPageDebounced = useCallback(useDebouncedPage((nextPage) => setPage(nextPage), 500), [setPage])
    const topRef = useRef(null);

    if (loading) {
        return <div style={{
            position: 'absolute', left: '50%', top: '50%',
            transform: 'translate(-50%, -50%)'
        }}>
            <Loader
                className="spinner mx-auto"
                type="TailSpin"
                color="#3A435E"
                height={70}
                width={70}
            />
        </div>
    } else {
        let paginationItems = [];
        const pageLimit = 10;
        const currentYear = new Date().getFullYear();
        const callPlaceholder = langCode === "gr" ? `π.χ.: ${currentYear}-1` : `ex: ${currentYear}-1`;
        const hasMandatoryFilters = Boolean(departmentSelected && dateSelected);
        const sortingOptions = EvaluationListTranslation("sorting_opts", langCode)['label'];
        const selectedSortingLabel = sortingOptions.find(i => i.value === sortSelected)?.label || "";
        const selectedDepartmentalCoordinatorLabel = (departmentalCoordinators || []).find(
            (coordinator) => String(coordinator?.id) === String(departmentalCoordinatorSelected)
        )?.label || "";
        const selectedCountryLabel = (countries || []).find(
            (country) => String(country?.id) === String(countrySelected)
        )?.countryName || "";
        const selectedDepartmentalCoordinatorId = departmentalCoordinatorSelected ? String(departmentalCoordinatorSelected) : null;
        const rows = (evaluation || []).reduce((acc, application) => {
            (application?.prefferedMobility || []).forEach((mobility, prefIndex) => {
                if (
                    selectedDepartmentalCoordinatorId &&
                    String(mobility?.iiaId?.departmentalCoordinatorContacts?.[1]?.id) !== selectedDepartmentalCoordinatorId
                ) {
                    return;
                }

                acc.push({
                    key: `${application?.id}_${prefIndex}`,
                    application,
                    mobility,
                    prefIndex,
                    finalScore: application?.evaluationData?.[prefIndex]?.finalScore,
                    preferenceOrder: mobility?.preferenceOrder
                })
            })
            return acc
        }, []);

        const getSortValue = (value) => value === null || value === undefined ? "" : String(value);
        const sortRows = (values) => {
            if (!sortSelected) {
                return values;
            }

            const sortedValues = [...values];
            const compare = (valueA, valueB, direction) => {
                const comparison = getSortValue(valueA).localeCompare(getSortValue(valueB), undefined, { numeric: true, sensitivity: "base" });
                return direction === "asc" ? comparison : comparison * -1;
            }

            if (sortSelected === "order_asc") {
                sortedValues.sort((a, b) => compare(a?.preferenceOrder, b?.preferenceOrder, "asc"));
            } else if (sortSelected === "order_desc") {
                sortedValues.sort((a, b) => compare(a?.preferenceOrder, b?.preferenceOrder, "desc"));
            } else if (sortSelected === "evaluation_asc") {
                sortedValues.sort((a, b) => compare(a?.finalScore, b?.finalScore, "asc"));
            } else if (sortSelected === "evaluation_desc") {
                sortedValues.sort((a, b) => compare(a?.finalScore, b?.finalScore, "desc"));
            }

            return sortedValues;
        }

        const sortedRows = sortRows(rows);
        const totalRows = sortedRows.length;
        const paginatedRows = sortedRows.slice(page * pageLimit, (page + 1) * pageLimit);

        if (page === 0) {
            for (let i = 0; i < (Math.ceil(totalRows / pageLimit) > 3 ? 3 : Math.ceil(totalRows / pageLimit)); i++) {
                paginationItems.push(
                    <Pagination.Item key={i} active={i === page} onClick={(event) => { setPageDebounced(i); topRef.current.scrollIntoView({ behavior: 'smooth' }) }}>
                        {i + 1}
                    </Pagination.Item>
                );
            }
        } else if (page === Math.ceil(totalRows / pageLimit) - 1) {
            for (let i = Math.ceil(totalRows / pageLimit) - 1; i >= Math.ceil(totalRows / pageLimit) - 3 && i >= 0; i--) {
                paginationItems.push(
                    <Pagination.Item key={i} active={i === page} onClick={(event) => { setPageDebounced(i); topRef.current.scrollIntoView({ behavior: 'smooth' }) }}>
                        {i + 1}
                    </Pagination.Item>
                );
                paginationItems.sort((element1, element2) => {
                    if (element1.key < element2.key) return -1;
                    if (element1.key > element2.key) return 1;
                    return 0;
                })
            }
        } else {
            for (let i = page - 1; i < page + 2; i++) {
                paginationItems.push(
                    <Pagination.Item key={i} active={i === page} onClick={(event) => { setPageDebounced(i); topRef.current.scrollIntoView({ behavior: 'smooth' }) }}>
                        {i + 1}
                    </Pagination.Item>
                );
            }
        }
        function setFieldValue(setterFunc) {
            return function (item,value) {
                setterFunc(value)
            }
        }

        return (
            <div className="py-5" ref={topRef}>
                <div className="container-xl">
                    <div className="row">
                        <div className="col-md-3 bg-light pt-2 filter-sidebar mx-auto h-100">
                            <div className="row">
                                <div className="col-md-12">
                                    <p className="font-weight-bold text-center">{EvaluationListTranslation("mandatoryFilters", langCode)['label']}</p>
                                </div>
                                <div className="form-group">
                                    {
                                        DropdownSelect({
                                            optionsList: departments ? langCode === "gr" ? departments.map(i => ({ label: i?.nameGR, value: i?.id })) : langCode === "en" ? departments.map(i => ({ label: i?.name, value: i?.id })) : [] : [],
                                            parentSetValueSelected: setFieldValue(setDepartmentSelected),
                                            parentValueSelected: departmentSelected,
                                            selectLabel: EvaluationListTranslation("department", langCode)['label'],
                                            required: true,
                                            componentId: "departmentSelected"
                                        })
                                    }
                                </div>
                                <div className="form-group">
                                    <label for="dateSelected">{EvaluationListTranslation("call", langCode)['label']}</label>
                                    <input type='text'
                                        value={dateSelected ? dateSelected : ""}
                                        className="form-control"
                                        id="dateSelected"
                                        placeholder={callPlaceholder}
                                        onChange={(e) => {
                                            if (e !== null) {
                                                setDateSelected(e.target.value)
                                            }
                                            else {
                                                setDateSelected(null)
                                            }
                                        }}
                                    />
                                </div>
                                <div className="col-md-12">
                                    <p className="font-weight-bold text-center">{EvaluationListTranslation("sorting", langCode)['label']}</p>
                                </div>
                                <div className="form-group">
                                    <SelectWithLabel
                                        value={selectedSortingLabel}
                                        onSelectChange={(e) => {
                                            if (e !== null) {
                                                setSortSelected(e.value);
                                            } else {
                                                setSortSelected(null);
                                            }
                                            setPage(0);
                                        }}
                                        id="sortSelected"
                                        placeholderText={EvaluationListTranslation("sorting", langCode)['label']}
                                        clearable={true}
                                        listOptions={sortingOptions}
                                    >
                                        {EvaluationListTranslation("sorting", langCode)['label']}
                                    </SelectWithLabel>
                                </div>
                                <div className="col-md-12">
                                    <p className="font-weight-bold text-center">{EvaluationListTranslation("optionalFilters", langCode)['label']}</p>
                                </div>
                                <div className="form-group">
                                    <label for="universitySelected">{EvaluationListTranslation("universityName", langCode)['label']}</label>
                                    <input type='text'
                                        value={universitySelected ? universitySelected : ""}
                                        className="form-control"
                                        id="universitySelected"
                                        onChange={(e) => {
                                            if (e !== null) {
                                                setUniversitySelected(e.target.value)
                                            }
                                            else {
                                                setUniversitySelected(null)
                                            }
                                        }}
                                    />
                                </div>
                                <div className="form-group">
                                    <label for="erasmusCodeSelected">{EvaluationListTranslation("erasmusCode", langCode)['label']}</label>
                                    <input type='text'
                                        value={erasmusCodeSelected ? erasmusCodeSelected : ""}
                                        className="form-control"
                                        id="erasmusCodeSelected"
                                        onChange={(e) => {
                                            if (e !== null) {
                                                setErasmusCodeSelected(e.target.value)
                                            }
                                            else {
                                                setErasmusCodeSelected(null)
                                            }
                                        }}
                                    />
                                </div>
                                <div className="form-group">
                                    <SelectWithLabel
                                        value={selectedCountryLabel}
                                        onSelectChange={(e) => {
                                            if (e !== null) {
                                                setCountrySelected(e.value);
                                            } else {
                                                setCountrySelected(null);
                                            }
                                            setPage(0);
                                        }}
                                        id="countrySelected"
                                        placeholderText={EvaluationListTranslation("country", langCode)['label']}
                                        clearable={true}
                                        listOptions={(countries || []).map((country) => ({
                                            label: country?.countryName,
                                            value: country?.id
                                        }))}
                                    >
                                        {EvaluationListTranslation("country", langCode)['label']}
                                    </SelectWithLabel>
                                </div>
                                <div className="form-group">
                                    <SelectWithLabel
                                        value={selectedDepartmentalCoordinatorLabel}
                                        onSelectChange={(e) => {
                                            if (e !== null) {
                                                setDepartmentalCoordinatorSelected(e.value);
                                            } else {
                                                setDepartmentalCoordinatorSelected(null);
                                            }
                                            setPage(0);
                                        }}
                                        id="departmentalCoordinatorSelected"
                                        placeholderText={EvaluationListTranslation("departmentalCoordinatorFilter", langCode)['label']}
                                        clearable={true}
                                        listOptions={(departmentalCoordinators || []).map((coordinator) => ({
                                            label: coordinator?.label,
                                            value: coordinator?.id
                                        }))}
                                    >
                                        {EvaluationListTranslation("departmentalCoordinatorFilter", langCode)['label']}
                                    </SelectWithLabel>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-9 pl-3 mx-auto">
                            {!hasMandatoryFilters ? <div className="d-flex justify-content-center">
                                <p>{EvaluationListTranslation("mandatoryFiltersMessage", langCode)['label']}</p>
                            </div> : (totalRows > 0) ? <div id="no-more-tables">
                                <table className="table-striped table-condensed cf table-responsive">
                                    <thead className="cf" style={{ backgroundColor: "#3A435E", color: "white" }}>
                                        <tr>
                                            <th scope="col" className="col-md-1">{EvaluationListTranslation("fullNameList", langCode)['label']}</th>
                                            <th scope="col" className="col-md-1">{EvaluationListTranslation("erasmusCode", langCode)['label']}</th>
                                            <th scope="col" className="col-md-1">{EvaluationListTranslation("universityName", langCode)['label']}</th>
                                            <th scope="col" className="col-md-1">{EvaluationListTranslation("iia", langCode)['label']}</th>
                                            <th scope="col" className="col-md-1">{EvaluationListTranslation("order", langCode)['label']}</th>
                                            <th scope="col" className="col-md-1">{EvaluationListTranslation("evaluation", langCode)['label']}</th>
                                            <th scope="col" className="col-md-2 text-center">{EvaluationListTranslation("approval", langCode)['label']}</th>
                                            <th scope="col" className="col-md-1">{EvaluationListTranslation("actions", langCode)['label']}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedRows.map((row) => {
                                            const iia = row?.mobility?.iiaId?.studentMobilitiesForStudies?.find(k => k?.fromInstitution?.heiId === process.env.REACT_APP_LOCAL_HEI_ID)

                                            return (
                                                <tr className="text-center" key={row.key}>
                                                    <td data-title="Full Name">{langCode === 'en' ? row?.application?.studentData?.[0]?.familyNameLatin + " " + row?.application?.studentData?.[0]?.nameLatin : row?.application?.studentData?.[0]?.familyNameGreek + " " + row?.application?.studentData?.[0]?.nameGreek}</td>
                                                    <td data-title="Erasmus Code">{row?.mobility?.institution?.erasmusCode}</td>
                                                    <td data-title="University">{row?.mobility?.institution?.name}</td>
                                                    <td data-title="IIA">
                                                        <>Mobilities: {iia?.mobilitiesPerYear}</><br />
                                                        <>Months: {iia?.totalMonthsPerYear}</><br />
                                                        <>Study Cycle: {iia?.studyCycles?.map((k, l) => {
                                                            if (l + 1 === iia?.studyCycles?.length) {
                                                                return <>{k.studyCycle}</>;
                                                            }
                                                            return <>{k.studyCycle}, </>;
                                                        })}</>
                                                    </td>
                                                    <td data-title="Preference order">{row?.preferenceOrder}</td>
                                                    <td data-title="Evaluation">{row?.finalScore}</td>
                                                    <td data-title="Approval">{row?.prefIndex + 1}</td>
                                                    <td data-title="Actions">
                                                        <div style={{ display: "flex" }}>
                                                            <div onClick={() => {
                                                                setSelectedRow(row)
                                                                setIsModalOpen(true)
                                                            }}><i className='fa fa-eye fa-primary m-2'></i></div>
                                                            <Restricted to="application_edit">
                                                            <div onClick={() => {
                                                                setSelectedRow(row)
                                                                setIsModalEditOpen(true)
                                                            }}><i className='fa fa-pencil fa-primary m-2'></i></div>
                                                            </Restricted>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div> : <div className="d-flex justify-content-center">
                                <p>{EvaluationListTranslation("noStudiesFound", langCode)['label']}</p>
                            </div>}
                            <div>
                                {isModalOpen && selectedRow && (
                                    <EvaluationModal
                                        show={isModalOpen}
                                        langCode={langCode}
                                        onClose={() => {
                                            setIsModalOpen(false)
                                            setSelectedRow(null)
                                        }}
                                        documentNumber={selectedRow?.application?.applicationNumber}
                                        studySemester={selectedRow?.application?.studiesData?.[selectedRow?.prefIndex]?.semesterDuringMobility}
                                        country={selectedRow?.mobility?.country?.countryName}
                                        agreementOfficer={selectedRow?.mobility?.iiaId?.departmentalCoordinatorContacts?.find(coord=>coord?.roles?.[0]?.value==="Departmental Coordinator")}
                                        department={selectedRow?.mobility?.iiaId?.departmentalCoordinatorContacts?.find(coord=>coord?.roles?.[0]?.value==="Departmental Coordinator")?.department?.name}
                                        language={selectedRow?.application?.languageData?.[selectedRow?.prefIndex]?.language?.languageName}
                                        languageDescription={selectedRow?.application?.languageData?.[selectedRow?.prefIndex]?.certificateLevel}
                                        languageLevel={selectedRow?.application?.evaluationData?.[selectedRow?.prefIndex]?.v1 * selectedRow?.application?.evaluationData?.[selectedRow?.prefIndex]?.w1}
                                        averageGrade="8.17"
                                        studyYearWeight="0.95"
                                        incentive="1"
                                        comments="Ο υποψήφιος κατέχει άριστη γνώση της αγγλικής γλώσσας που είναι σημαντικό για να παρακολουθήσει τα μαθήματα στο πανεπιστήμιο το οποίο προσφέρει προγράμματα σπουδών στην αγγλική."

                                    />
                                )}
                                {isModalEditOpen && selectedRow && (
                                    <EvaluationEditModal
                                        show={isModalEditOpen}
                                        langCode={langCode}
                                        onClose={() => {
                                            setIsModalEditOpen(false)
                                            setSelectedRow(null)
                                        }}
                                        selectedRow={selectedRow}
                                        userService={props.userService}
                                    />
                                )}
                            </div>
                            {
                                totalRows !== 0
                                    ?
                                    <div className="d-flex justify-content-center m-2">
                                        {/* feature idea do not show previous button if the user is in the first or last page etc */}
                                        <Pagination>
                                            <Pagination.First onClick={() => { if (totalRows !== 0) setPageDebounced(0); topRef.current.scrollIntoView({ behavior: 'smooth' }) }} />
                                            <Pagination.Prev onClick={() => { if (page !== 0) setPageDebounced(page - 1); topRef.current.scrollIntoView({ behavior: 'smooth' }) }} />
                                            {paginationItems.length > 3 ? <Pagination.Ellipsis /> : <></>}
                                            {paginationItems}
                                            {paginationItems.length > 3 ? <Pagination.Ellipsis /> : <></>}
                                            <Pagination.Next onClick={() => { if (page !== Math.ceil(totalRows / pageLimit) - 1) setPageDebounced(page + 1); topRef.current.scrollIntoView({ behavior: 'smooth' }) }} />
                                            <Pagination.Last onClick={() => { setPageDebounced(Math.ceil(totalRows / pageLimit) - 1); topRef.current.scrollIntoView({ behavior: 'smooth' }) }} />
                                        </Pagination>
                                    </div>
                                    :
                                    <></>
                            }
                        </div>
                    </div>
                </div>
            </div >
        )
    }
}
